import { E3Pause, E3Play } from '@e3dc-react/icons'
import { Box } from '@mui/material'
import { debounce } from 'lodash'
import React from 'react'
import { Loading } from '@e3dc-react/shell/Elements/Loading'
import { usePreviousPersistent } from '@e3dc-react/shell/Hooks/usePrevious'
import { ChargingStatus } from 'src/Routes/EMobility/EMobilityModels'

/**
 * A button that toggles the charging session status of a wallbox.
 */
const ToggleChargingSessionStatusButton: React.FC<{
  chargingStatus: ChargingStatus
  isChargingSessionPaused: boolean | undefined
  toggleWallboxChargingStatus: () => Promise<void>
}> = ({ chargingStatus, isChargingSessionPaused: currentIsChargingSessionPaused, toggleWallboxChargingStatus }) => {
  const previousIsChargingSessionPaused = usePreviousPersistent(currentIsChargingSessionPaused)

  const showPauseButton = (chargingStatus === 'CHARGING' || chargingStatus === 'WAIT_PV') && currentIsChargingSessionPaused === false
  const showResumeButton = currentIsChargingSessionPaused === true
  /* All this transitioning logic is necessary because there is a significant delay between the user clicking 'pause' and chargingStatus (which comes from the websocket connection) reflecting that change.  */
  const isWaitingForPv = chargingStatus === 'WAIT_PV' && currentIsChargingSessionPaused === false
  const isTransitioningFromChargingToPause = chargingStatus === 'CHARGING' && currentIsChargingSessionPaused === true
  const isTransitioningFromPauseToCharging =
    chargingStatus !== 'CHARGING' && previousIsChargingSessionPaused === true && currentIsChargingSessionPaused === false && !isWaitingForPv
  const isLoading = isTransitioningFromChargingToPause || isTransitioningFromPauseToCharging

  const debouncedToggleWallboxChargingStatus = debounce(toggleWallboxChargingStatus, 100)

  if (!isLoading && !showPauseButton && !showResumeButton) return null

  return (
    <Box
      component={'button'}
      sx={{
        all: 'unset',
        cursor: isLoading ? 'default' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: 'auto',
        minWidth: 30,
        '& svg': { fontSize: 30, color: 'text.primary' },
      }}
      onClick={e => {
        e.stopPropagation()
        debouncedToggleWallboxChargingStatus()
      }}
      disabled={isLoading}
    >
      {isLoading ? (
        <Loading
          circularProgress={{
            size: 18,
          }}
        />
      ) : (
        <>
          {showPauseButton && <E3Pause />}
          {showResumeButton && <E3Play />}
        </>
      )}
    </Box>
  )
}

export default ToggleChargingSessionStatusButton
